"use client"

import { Link } from "react-router-dom"
import { useState } from "react"
import Button from "./Button"
import Input from "./Input"
import Textarea from "./Textarea"
import { Card, CardContent } from "./Card"
import Slider from "./Slider"
import "./Finance.css"
import { Clock, FileText, CheckCircle, ShieldCheck, Menu, X } from "lucide-react"
import { getDatabase, ref, push, set } from "firebase/database"
import { useCurrentUid } from "../hooks/useCurrentUid"

const Icon = ({ name }) => {
  return <span className={`icon icon-${name}`}>{name}</span>
}

export default function FinancePage() {
  const userId = useCurrentUid()
  // State for EMI calculator
  const [loanAmount, setLoanAmount] = useState(100000)
  const [interestRate, setInterestRate] = useState(10)
  const [loanTenure, setLoanTenure] = useState(36)
  const [emi, setEmi] = useState(calculateEmi(100000, 10, 36))
  const [isNavOpen, setIsNavOpen] = useState(false);

  // Contact form states
  const [fullName, setFullName] = useState("")
  const [email, setEmail] = useState("")
  const [phone, setPhone] = useState("")
  const [message, setMessage] = useState("")
  const [statusMessage, setStatusMessage] = useState(null) // success or error message
  const [isSubmitting, setIsSubmitting] = useState(false)

  const toggleNav = () => {
    setIsNavOpen((prev) => {
      return !prev;
    });
  };

  // Calculate EMI
  function calculateEmi(principal, rate, time) {
    const r = rate / 12 / 100
    const t = time
    const emi = (principal * r * Math.pow(1 + r, t)) / (Math.pow(1 + r, t) - 1)
    return Math.round(emi)
  }

  // Update EMI when inputs change
  const updateEmi = () => {
    setEmi(calculateEmi(loanAmount, interestRate, loanTenure))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Basic validation
    if (!fullName.trim() || !email.trim() || !phone.trim() || !message.trim()) {
      setStatusMessage({ type: "error", text: "Please fill in all fields." })
      return
    }

    if (!userId) {
      setStatusMessage({ type: "error", text: "You must be logged in to submit a request." })
      return
    }

    setIsSubmitting(true)
    setStatusMessage(null)

    try {
      const database = getDatabase()
      const loanRequestRef = ref(database, `loanRequest/${userId}`)

      // Push new request to create a unique key under loanRequest/userId/
      const newRequestRef = push(loanRequestRef)

      await set(newRequestRef, {
        fullName: fullName.trim(),
        email: email.trim(),
        phone: phone.trim(),
        message: message.trim(),
        timestamp: Date.now(),
      })

      setStatusMessage({ type: "success", text: "Request submitted successfully!" })
      setTimeout(() => {
        setStatusMessage("")
      }, [2000])

      // Clear form fields
      setFullName("")
      setEmail("")
      setPhone("")
      setMessage("")
    } catch (error) {
      setStatusMessage({ type: "error", text: "Failed to submit request. Please try again later." })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="finance-page">
      {/* Header */}
      <header className="header">
        <div className="container">
          <div className="logo">
            <span className="logo-text">FinanceEase</span>
          </div>
          <button
            className="hamburger"
            onClick={toggleNav}
            aria-label={isNavOpen ? "Close navigation" : "Open navigation"}
            aria-expanded={isNavOpen}
          >
            {isNavOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
          <nav className={`nav ${isNavOpen ? "nav-open" : ""}`}>
            <Link to="/dashboard/loan-history" className="nav-link" onClick={() => setIsNavOpen(false)}>
              Your Applications
            </Link>
            {/* <a href="#calculator" className="nav-link" onClick={() => setIsNavOpen(false)}>
              Loan Calculator
            </a>
            <a href="#requirements" className="nav-link" onClick={() => setIsNavOpen(false)}>
              Requirements
            </a>
            <a href="#partners" className="nav-link" onClick={() => setIsNavOpen(false)}>
              Partners
            </a> */}
          </nav>
        </div>
      </header>
      {/* Hero Section */}
      <section id="home" className="hero">
        <div className="container">
          <div className="hero-content">
            {/* Left Side - Hero Text */}
            <div className="hero-text">
              <h1 className="hero-title">Financial Freedom Starts With The Right Loan</h1>
              <p className="hero-subtitle">
                Get quick approval on personal loans with competitive interest rates and flexible repayment options.
              </p>
              <div className="hero-buttons">
                <Link to="/dashboard/apply-loan">
                  <Button className="hero-button primary">Apply Now</Button>
                </Link>
              </div>
            </div>

            {/* Right Side - Contact Form (Instead of Image) */}
            <div className="contact-form-container">
              <h2 className="section-title">Connect With Us</h2>
              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-group">
                  <Input
                    type="text"
                    placeholder="Full Name"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    disabled={isSubmitting}
                    required
                  />
                </div>
                <div className="form-group">
                  <Input
                    type="email"
                    placeholder="Email Address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isSubmitting}
                    required
                  />
                </div>
                <div className="form-group">
                  <Input
                    type="tel"
                    placeholder="Phone Number"
                    value={phone}
                    maxLength="15"
                    onChange={(e) => setPhone(e.target.value)}
                    disabled={isSubmitting}
                    required
                  />
                </div>
                <div className="form-group">
                  <Textarea
                    placeholder="How can we help you?"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    disabled={isSubmitting}
                    required
                  />
                </div>
                <Button className="submit-button" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Submitting..." : "Submit Request"}
                </Button>

                {statusMessage && (
                  <p
                    style={{
                      color: statusMessage.type === "error" ? "red" : "green",
                      marginTop: "1rem",
                      textAlign: 'center'
                    }}
                  >
                    {statusMessage.text}
                  </p>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Why Choose Our Personal Loans?</h2>
            <p className="section-subtitle">
              We offer tailored financial solutions to meet your unique needs with transparent terms and competitive
              rates.
            </p>
          </div>

          <div className="features-grid">
            <Card className="feature-card">
              <CardContent>
                <div className="feature-header">
                  <span className="Icons">&#8377;</span>
                  <h3 className="feature-title">Competitive Rates</h3>
                </div>
                <p className="feature-description">
                  Enjoy interest rates starting from just 8.99% p.a. with transparent fee structure.
                </p>
              </CardContent>
            </Card>

            <Card className="feature-card">
              <CardContent>
                <div className="feature-header">
                  <Clock className="Icons" />
                  <h3 className="feature-title">Quick Approval</h3>
                </div>
                <p className="feature-description">
                  Get your loan approved within 24 hours and funds disbursed directly to your account.
                </p>
              </CardContent>
            </Card>

            <Card className="feature-card">
              <CardContent>
                <div className="feature-header">
                  <FileText className="Icons" />
                  <h3 className="feature-title">Minimal Documentation</h3>
                </div>
                <p className="feature-description">
                  Simple paperwork with digital KYC process for a hassle-free experience.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* EMI Calculator Section */}
      <section id="calculator" className="calculator-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Personal Loan EMI Calculator</h2>
            <p className="section-subtitle">
              Plan your finances better by calculating your monthly EMI based on loan amount, interest rate, and tenure.
            </p>
          </div>

          <div className="calculator-grid">
            <div className="calculator-inputs">
              <div className="slider-group">
                <label className="slider-label">Loan Amount: ₹{loanAmount.toLocaleString()}</label>
                <Slider
                  min={10000}
                  max={2000000}
                  step={10000}
                  defaultValue={[loanAmount]}
                  onValueChange={(value) => {
                    setLoanAmount(value[0])
                    updateEmi()
                  }}
                />
                <div className="slider-range">
                  <span>₹10,000</span>
                  <span>₹20,00,000</span>
                </div>
              </div>

              <div className="slider-group">
                <label className="slider-label">Interest Rate: {interestRate}%</label>
                <Slider
                  min={5}
                  max={24}
                  step={0.5}
                  defaultValue={[interestRate]}
                  onValueChange={(value) => {
                    setInterestRate(value[0])
                    updateEmi()
                  }}
                />
                <div className="slider-range">
                  <span>5%</span>
                  <span>24%</span>
                </div>
              </div>

              <div className="slider-group">
                <label className="slider-label">Loan Tenure: {loanTenure} months</label>
                <Slider
                  min={6}
                  max={60}
                  step={6}
                  defaultValue={[loanTenure]}
                  onValueChange={(value) => {
                    setLoanTenure(value[0])
                    updateEmi()
                  }}
                />
                <div className="slider-range">
                  <span>6 months</span>
                  <span>60 months</span>
                </div>
              </div>
            </div>

            <div className="calculator-results">
              <div className="emi-display">
                <h3 className="emi-title">Your Monthly EMI</h3>
                <div className="emi-amount">₹{emi.toLocaleString()}</div>
              </div>

              <div className="emi-details">
                <div className="emi-detail-item">
                  <p className="emi-detail-label">Total Interest</p>
                  <p className="emi-detail-value">₹{Math.round(emi * loanTenure - loanAmount).toLocaleString()}</p>
                </div>
                <div className="emi-detail-item">
                  <p className="emi-detail-label">Total Amount</p>
                  <p className="emi-detail-value">₹{Math.round(emi * loanTenure).toLocaleString()}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Requirements Section */}
      <section id="requirements" className="requirements-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Loan Requirements & Process</h2>
            <p className="section-subtitle">
              We've simplified the loan application process to get you funded quickly with minimal hassle.
            </p>
          </div>

          <div className="requirements-grid">
            <div className="eligibility">
              <h3 className="subsection-title">Eligibility Requirements</h3>
              <ul className="eligibility-list">
                <li className="eligibility-item">
                  <CheckCircle className="Icons" />
                  <div>
                    <h4 className="eligibility-label">Age</h4>
                    <p className="eligibility-value">Between 21 and 65 years old</p>
                  </div>
                </li>
                <li className="eligibility-item">
                  <CheckCircle className="Icons" />
                  <div>
                    <h4 className="eligibility-label">Income</h4>
                    <p className="eligibility-value">Minimum monthly income of ₹15,000</p>
                  </div>
                </li>
                <li className="eligibility-item">
                  <CheckCircle className="Icons" />
                  <div>
                    <h4 className="eligibility-label">Employment</h4>
                    <p className="eligibility-value">Salaried individuals with at least 1 year of work experience</p>
                  </div>
                </li>
                <li className="eligibility-item">
                  <CheckCircle className="Icons" />
                  <div>
                    <h4 className="eligibility-label">Credit Score</h4>
                    <p className="eligibility-value">Minimum CIBIL score of 700</p>
                  </div>
                </li>
              </ul>
            </div>

            <div className="process">
              <h3 className="subsection-title">Application Process</h3>
              <div className="process-steps">
                <div className="process-step">
                  <div className="step-number">1</div>
                  <div>
                    <h4 className="step-title">Apply Online</h4>
                    <p className="step-description">Fill out our simple online application form with your details</p>
                  </div>
                </div>
                <div className="process-step">
                  <div className="step-number">2</div>
                  <div>
                    <h4 className="step-title">Document Verification</h4>
                    <p className="step-description">Submit required documents for verification</p>
                  </div>
                </div>
                <div className="process-step">
                  <div className="step-number">3</div>
                  <div>
                    <h4 className="step-title">Loan Approval</h4>
                    <p className="step-description">Get your loan approved within 24 hours</p>
                  </div>
                </div>
                <div className="process-step">
                  <div className="step-number">4</div>
                  <div>
                    <h4 className="step-title">Disbursement</h4>
                    <p className="step-description">Receive funds directly in your bank account</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="documents-section">
            <h3 className="subsection-title">Required Documents</h3>
            <div className="documents-grid">
              <div className="document-item">
                <FileText className="Icons" />
                <p className="document-description">Identity Proof (Aadhaar, PAN, Passport)</p>
              </div>
              <div className="document-item">
                <FileText className="Icons" />
                <p className="document-description">Address Proof (Utility bills, Rental agreement)</p>
              </div>
              <div className="document-item">
                <FileText className="Icons" />
                <p className="document-description">Income Proof (Salary slips, Bank statements)</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section id="partners" className="partners-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Our Trusted Partners</h2>
            <p className="section-subtitle">
              We collaborate with leading financial institutions to bring you the best loan options.
            </p>
          </div>

          <div className="partners-grid">
            <div className="partner-logo">
              <img src="/Images/utkarsh.jpeg" alt="Utkarsh Partner" />
            </div>
          </div>

          <div className="trust-banner">
            <div className="trust-content">
              <ShieldCheck className="Icons" />
              <div>
                <h3 className="trust-title">Trusted by 10,000+ Customers</h3>
                <p className="trust-description">
                  Join thousands of satisfied customers who've achieved their financial goals
                </p>
              </div>
            </div>
            <Link to="/dashboard/apply-loan">
              <Button className="hero-button primary">Apply Now</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <h2 className="cta-title">Ready to Get Started?</h2>
          <p className="cta-description">
            Apply now and get your loan approved within 24 hours. Our financial experts are ready to help you.
          </p>
          <div className="cta-buttons">
            <Link to="/dashboard/apply-loan">
              <Button className="hero-button primary">Apply Now</Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
